import React, { useContext, useEffect } from 'react';
import { useForm, FormProvider, UseFormSetError, FieldValues } from 'react-hook-form';
import classNames from 'classnames';

import useApi, { UseInvalidateEndpoint } from 'common/useApi';
import useFormApiErrors from 'common/useFormApiErrors';

import validationPatterns from 'common/validationPatterns';

import FormLabel from 'components/FormLabel';
import Loading from 'components/Loading';
import AuthContext from 'common/auth';

const AccountSettings = () => {
  const { isLoading, isFetching, isError, isSuccess, error, mutate } = useApi('post', '/api/me/update-email');
  const { user } = useContext(AuthContext)!;
  const invalidate = UseInvalidateEndpoint();
  const formMethods = useForm({
    defaultValues: { email: user?.email ?? '', token: '' },
    mode: 'onChange',
  });
  const { handleSubmit, register, formState: { isSubmitting, isDirty, isValid }, setError, setValue } = formMethods;

  const { email } = validationPatterns;

  // S12: changing the email (login id + password-reset target) requires an OTP.
  const onSubmit = ({ email, token }: { email: string, token: string }) => {
    mutate({ email, token });
  };

  const messageInvalid = 'A valid email is required';
  const messageRequired = 'Email can\'t be blank';

  useFormApiErrors(
    isError,
    error,
    setError as unknown as UseFormSetError<FieldValues>,
    isSuccess,
    'New email saved — check your inbox to verify the new address',
  );

  useEffect(() => {
    if (isSuccess) {
      invalidate('/api/me'); // is_verified / date_verified changed
      setValue('token', '');
    }
  }, [isSuccess]);

  const formDisabled = isSubmitting || isLoading || isFetching || (!isValid && isDirty);

  return (
    <>
      <h3>
        Account Settings
        {!user?.has_device
          && <>
            <br />
            <small className='tfa-required'>Two-Factor Authentication is required to change your email address.</small>
          </>
        }
      </h3>
      <div
        className={classNames({
          box: true,
          isLoading,
        })}
      >
        {isLoading && <Loading />}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormLabel field='email' className='half'>
              <input
                type='email'
                placeholder='email@example.com'
                {...register('email', {
                  pattern: {
                    message: messageInvalid,
                    value: email,
                  },
                  required: messageRequired,
                })}
                disabled={!user?.has_device}
              />
            </FormLabel>
            <FormLabel field='token' name='Enter the one-time password generated by your OTP app' className='half'>
              <input
                type='text'
                placeholder='######'
                {...register('token', {
                  minLength: {
                    message: 'Token must be exactly 6 characters long',
                    value: 6,
                  },
                  maxLength: {
                    message: 'Token must be exactly 6 characters long',
                    value: 6,
                  },
                  required: 'Token can\'t be blank',
                })}
                disabled={!user?.has_device}
              />
            </FormLabel>
            <button
              type='submit'
              className='primary big'
              disabled={!user?.has_device || formDisabled}
            >
              Save Changes
            </button>
          </form>
        </FormProvider>
      </div>
    </>
  );

};

export default AccountSettings;
